<template>
  <div class="container mt-4">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
     <h4 class="mb-4" style="color: #b03a45;">
  <i class="fas fa-gift me-2" style="color: #b03a45;"></i> {{ $t('common.referenceData.addUpdateBonus') }}
</h4>


      <!-- Formulaire -->
      <form @submit.prevent="handleSubmit">
        <div class="row">
          <div class="col-md-4 mb-3">
            <label class="form-label fw-bold text-dark">{{ $t('common.referenceData.employee') }}</label>
            <select class="form-select" v-model="form.user_id" required>
              <option disabled value="">{{ $t('common.referenceData.selectEmployee') }}</option>
              <option v-for="user in users" :key="user.id" :value="user.id">
                {{ user.firstname }} {{ user.lastname }}
              </option>
            </select>
          </div>

          <div class="col-md-4 mb-3">
            <label class="form-label fw-bold text-dark">{{ $t('common.referenceData.project') }}</label>
            <select class="form-select" v-model="form.project_id">
              <option :value="null">{{ $t('common.referenceData.optional') }}</option>
              <option v-for="project in projects" :key="project.id" :value="project.id">
                {{ project.name }}
              </option>
            </select>
          </div>

          <div class="col-md-4 mb-3">
            <label class="form-label fw-bold text-dark">{{ $t('common.referenceData.amount') }}</label>
            <input type="number" class="form-control" v-model="form.amount" required />
          </div>

          <div class="col-md-4 mb-3">
            <label class="form-label fw-bold text-dark">{{ $t('common.referenceData.maxBonus') }}</label>
            <input type="number" class="form-control" v-model="form.max_bonus" />
          </div>

          <div class="col-md-4 mb-3">
            <label class="form-label fw-bold text-dark">{{ $t('common.referenceData.otherBonus') }}</label>
            <input type="number" class="form-control" v-model="form.other_bonus" />
          </div>

          <div class="col-md-4 mb-3">
  <label class="form-label fw-bold text-dark">{{ $t('common.referenceData.bonusMonth') }}</label>
  <input type="month" class="form-control" v-model="form.bonus_month" />
</div>

        </div>

        <div class="d-flex gap-3 mt-3">
          <button type="submit" class="btn text-white px-4" style="background-color: #b03a45;">
            {{ editingId ? $t('common.buttons.update') : $t('common.buttons.add') }}
          </button>
          <button type="button" class="btn btn-secondary px-4" @click="resetForm">{{ $t('common.buttons.cancel') }}</button>
        </div>
      </form>

      <!-- Liste des bonus -->
      <hr class="my-4" />
      <h5 class="mb-3" style="color: #b03a45;">
  <i class="fas fa-list me-2" style="color: #b03a45;"></i> {{ $t('common.referenceData.existingBonuses') }}
</h5>


      <div v-if="bonuses.length" class="table-responsive">
        <table class="table table-bordered align-middle">
          <thead class="table-light">
            <tr>
              <th>{{ $t('common.referenceData.employee') }}</th>
              <th>{{ $t('common.referenceData.project') }}</th>
              <th>{{ $t('common.referenceData.amount') }}</th>
              <th>{{ $t('common.referenceData.date') }}</th>
              <th>{{ $t('common.referenceData.status') }}</th>
              <th class="text-center">{{ $t('common.referenceData.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="bonus in bonuses" :key="bonus.id">
              <td>{{ bonus.user?.firstname }} {{ bonus.user?.lastname }}</td>
              <td>{{ bonus.project?.name || '-' }}</td>
              <td>{{ bonus.amount }} DT</td>
              <td>{{ bonus.bonus_month }}</td>

              <td>
                <span :class="bonus.is_active ? 'text-success' : 'text-danger'">
                  {{ bonus.is_active ? $t('common.referenceData.active') : $t('common.referenceData.inactive') }}
                </span>
              </td>
              <td class="text-center">
                <button class="btn btn-outline-danger btn-sm me-2" @click="loadBonus(bonus)">{{ $t('common.referenceData.edit') }}</button>
                <button class="btn btn-outline-secondary btn-sm me-2" @click="deleteBonus(bonus.id)">{{ $t('common.referenceData.delete') }}</button>
                <button class="btn btn-outline-dark btn-sm" @click="toggleBonus(bonus.id)">{{ $t('common.referenceData.toggle') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="text-muted">{{ $t('common.referenceData.noBonuses') }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from '@/api'
import { useToast } from 'vue-toastification'

const toast = useToast()

const users = ref([])
const projects = ref([])
const bonuses = ref([])

const form = ref({
  user_id: '',
  project_id: null,
  amount: '',
  max_bonus: '',
  other_bonus: '',
  bonus_month: '',
  is_active: true
})

const editingId = ref(null)

const fetchUsers = async () => {
  const { data } = await axios.get('/getAllUser')
  users.value = data.data || []
}

const fetchProjects = async () => {
  const { data } = await axios.get('/projects')
  projects.value = data.data || data || []
}

const fetchBonuses = async () => {
  const { data } = await axios.get('/employee/bonuses')
  bonuses.value = data.data || []
}

const handleSubmit = async () => {
  try {
    if (editingId.value) {
      await axios.put(`/employee/bonuses/${editingId.value}`, form.value)
      toast.success($t('common.referenceData.bonusUpdated'))
    } else {
      await axios.post('/employee/bonuses', form.value)
      toast.success($t('common.referenceData.bonusAdded'))
    }
    resetForm()
    fetchBonuses()
  } catch (err) {
    toast.error($t('common.referenceData.errorSavingBonus'))
    console.error(err)
  }
}

const loadBonus = (bonus) => {
  editingId.value = bonus.id
  form.value = {
    user_id: bonus.user_id,
    project_id: bonus.project_id,
    amount: bonus.amount,
    max_bonus: bonus.max_bonus,
    other_bonus: bonus.other_bonus,
    bonus_month: bonus.bonus_month,

    is_active: bonus.is_active
  }
}

const resetForm = () => {
  editingId.value = null
  form.value = {
    user_id: '',
    project_id: null,
    amount: '',
    max_bonus: '',
    other_bonus: '',
    bonus_date: '',
    is_active: true
  }
}

const deleteBonus = async (id) => {
  try {
    await axios.delete(`/employee/bonuses/${id}`)
    toast.success($t('common.referenceData.bonusDeleted'))
    fetchBonuses()
  } catch (err) {
    toast.error($t('common.referenceData.errorDeletingBonus'))
  }
}

const toggleBonus = async (id) => {
  try {
    await axios.patch(`/employee/bonuses/change-status/${id}`)
    toast.success($t('common.referenceData.statusChanged'))
    fetchBonuses()
  } catch (err) {
    toast.error($t('common.referenceData.toggleFailed'))
  }
}

onMounted(() => {
  fetchUsers()
  fetchProjects()
  fetchBonuses()
})
</script>
